import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_URL } from '../api/api';
import {
    Box, Typography, Paper, Button, TextField, Select, MenuItem,
    FormControl, InputLabel, Alert, CircularProgress, Table, TableBody,
    TableCell, TableContainer, TableHead, TableRow, Dialog, DialogTitle,
    DialogContent, DialogActions, Checkbox, FormControlLabel, Grid, Stack,
    Chip, IconButton, FormGroup, InputAdornment
} from '@mui/material';
import {
    AdminPanelSettings as AdminPanelSettingsIcon,
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Security as SecurityIcon,
    Person as PersonIcon,
    Search as SearchIcon
} from '@mui/icons-material';

const AdminPage = () => {
    const [users, setUsers] = useState([]);
    const [companies, setCompanies] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [editingUser, setEditingUser] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');

    const [formData, setFormData] = useState({
        username: '',
        password: '',
        role: 'user',
        company_id: '',
        permissions: {
            upload: false,
            dashboard: true,
            reports: true,
            settings: false,
            admin: false
        }
    });

    const token = localStorage.getItem('token');

    const fetchUsers = async () => {
        setLoading(true);
        try {
            const response = await axios.get(`${API_URL}/auth/users`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setUsers(response.data);
            setError(null);
        } catch (err) {
            setError('Erro ao carregar usuários: ' + (err.response?.data?.message || err.message));
        } finally {
            setLoading(false);
        }
    };

    const fetchCompanies = async () => {
        try {
            const response = await axios.get(`${API_URL}/companies`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setCompanies(response.data.filter(c => c.active));
        } catch (err) {
            console.error('Erro ao carregar empresas:', err);
        }
    };

    useEffect(() => {
        fetchUsers();
        fetchCompanies();
    }, []);

    // Filter users based on search term
    const filteredUsers = users.filter(user =>
        user.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.role.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const handleOpenModal = (user = null) => {
        if (user) {
            setEditingUser(user);
            setFormData({
                username: user.username,
                password: '',
                role: user.role,
                company_id: user.company_id || '',
                permissions: typeof user.permissions === 'object' ? user.permissions : {}
            });
        } else {
            setEditingUser(null);
            setFormData({
                username: '',
                password: '',
                role: 'user',
                company_id: '',
                permissions: {
                    upload: false,
                    dashboard: true,
                    reports: true,
                    settings: false,
                    admin: false
                }
            });
        }
        setShowModal(true);
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setEditingUser(null);
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handlePermissionChange = (perm) => {
        setFormData(prev => ({
            ...prev,
            permissions: {
                ...prev.permissions,
                [perm]: !prev.permissions[perm]
            }
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const payload = { ...formData };
            if (!payload.password) delete payload.password;

            if (editingUser) {
                await axios.put(`${API_URL}/auth/users/${editingUser.id}`, payload, {
                    headers: { Authorization: `Bearer ${token}` }
                });
            } else {
                await axios.post(`${API_URL}/auth/users`, payload, {
                    headers: { Authorization: `Bearer ${token}` }
                });
            }
            fetchUsers();
            handleCloseModal();
        } catch (err) {
            alert('Erro ao salvar usuário: ' + (err.response?.data?.message || err.message));
        }
    };

    const handleDelete = async (id) => {
        if (window.confirm('Tem certeza que deseja excluir este usuário?')) {
            try {
                await axios.delete(`${API_URL}/auth/users/${id}`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                fetchUsers();
            } catch (err) {
                alert('Erro ao excluir usuário: ' + (err.response?.data?.message || err.message));
            }
        }
    };

    const renderPermissions = (permissions) => {
        if (!permissions || typeof permissions !== 'object') return <Chip label="Nenhuma" size="small" />;

        const activePerms = Object.entries(permissions).filter(([_, val]) => val).map(([key]) => key);

        if (activePerms.length === 0) return <Chip label="Nenhuma" size="small" />;

        return (
            <Stack direction="row" spacing={1}>
                {activePerms.map(perm => (
                    <Chip key={perm} label={perm} size="small" color="primary" variant="outlined" />
                ))}
            </Stack>
        );
    };

    if (loading) return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
            <CircularProgress />
        </Box>
    );

    if (error) return (
        <Box sx={{ p: 3 }}>
            <Alert severity="error">{error}</Alert>
        </Box>
    );

    return (
        <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 4 }}>
                <Box>
                    <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold', color: 'text.primary', display: 'flex', alignItems: 'center' }}>
                        <AdminPanelSettingsIcon sx={{ mr: 2, color: 'primary.main' }} /> Administração de Usuários
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                        Gerencie usuários e suas permissões de acesso
                    </Typography>
                </Box>
                <Stack direction="row" spacing={2} alignItems="center">
                    <TextField
                        size="small"
                        placeholder="Buscar usuário..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon color="action" />
                                </InputAdornment>
                            ),
                        }}
                        sx={{ width: 200 }}
                    />
                    <Button
                        variant="contained"
                        color="primary"
                        startIcon={<AddIcon />}
                        onClick={() => handleOpenModal()}
                    >
                        Novo Usuário
                    </Button>
                </Stack>
            </Stack>

            <Paper sx={{ width: '100%', overflow: 'hidden' }}>
                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow sx={{ bgcolor: 'action.hover' }}>
                                <TableCell sx={{ fontWeight: 'bold' }}>ID</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>Usuário</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>Papel</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>Permissões</TableCell>
                                <TableCell align="center" sx={{ fontWeight: 'bold' }}>Ações</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {filteredUsers.map(user => (
                                <TableRow key={user.id} hover>
                                    <TableCell>{user.id}</TableCell>
                                    <TableCell>
                                        <Typography fontWeight="bold">{user.username}</Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            icon={user.role === 'admin' ? <SecurityIcon /> : <PersonIcon />}
                                            label={user.role === 'admin' ? 'Admin' : 'Usuário'}
                                            color={user.role === 'admin' ? 'secondary' : 'default'}
                                            size="small"
                                        />
                                    </TableCell>
                                    <TableCell>
                                        {renderPermissions(user.permissions)}
                                    </TableCell>
                                    <TableCell align="center">
                                        <IconButton
                                            onClick={() => handleOpenModal(user)}
                                            color="primary"
                                            size="small"
                                            sx={{ mr: 1 }}
                                        >
                                            <EditIcon />
                                        </IconButton>
                                        <IconButton
                                            onClick={() => handleDelete(user.id)}
                                            color="error"
                                            size="small"
                                        >
                                            <DeleteIcon />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
                {filteredUsers.length === 0 && (
                    <Box sx={{ p: 3, textAlign: 'center' }}>
                        <Typography color="text.secondary">
                            {searchTerm ? 'Nenhum usuário encontrado para a busca' : 'Nenhum usuário cadastrado'}
                        </Typography>
                    </Box>
                )}
            </Paper>

            <Dialog open={showModal} onClose={handleCloseModal} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ fontWeight: 'bold' }}>
                    {editingUser ? 'Editar Usuário' : 'Novo Usuário'}
                </DialogTitle>
                <form onSubmit={handleSubmit}>
                    <DialogContent>
                        <Stack spacing={3}>
                            <TextField
                                label="Usuário"
                                name="username"
                                value={formData.username}
                                onChange={handleInputChange}
                                fullWidth
                                required
                                disabled={editingUser !== null}
                            />
                            <TextField
                                label="Senha"
                                name="password"
                                type="password"
                                value={formData.password}
                                onChange={handleInputChange}
                                fullWidth
                                required={!editingUser}
                                helperText={editingUser ? "Deixe em branco para manter a senha atual" : ""}
                            />
                            <FormControl fullWidth>
                                <InputLabel>Papel</InputLabel>
                                <Select
                                    name="role"
                                    value={formData.role}
                                    label="Papel"
                                    onChange={handleInputChange}
                                >
                                    <MenuItem value="user">
                                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                            <PersonIcon sx={{ mr: 1, fontSize: 20 }} /> Usuário
                                        </Box>
                                    </MenuItem>
                                    <MenuItem value="admin">
                                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                            <SecurityIcon sx={{ mr: 1, fontSize: 20 }} /> Administrador
                                        </Box>
                                    </MenuItem>
                                </Select>
                            </FormControl>

                            {/* Company Selection - Required for non-admin users */}
                            {formData.role !== 'admin' && (
                                <FormControl fullWidth required>
                                    <InputLabel>Empresa</InputLabel>
                                    <Select
                                        name="company_id"
                                        value={formData.company_id}
                                        label="Empresa"
                                        onChange={handleInputChange}
                                    >
                                        <MenuItem value="">
                                            <em>Selecione uma empresa</em>
                                        </MenuItem>
                                        {companies.map(company => (
                                            <MenuItem key={company.id} value={company.id}>
                                                {company.name}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            )}

                            {formData.role === 'admin' && (
                                <Alert severity="info">
                                    Administradores não precisam estar vinculados a uma empresa específica.
                                </Alert>
                            )}

                            <Box>
                                <Typography variant="subtitle2" gutterBottom>Permissões</Typography>
                                <Paper variant="outlined" sx={{ p: 2 }}>
                                    <FormGroup>
                                        <Grid container>
                                            {['upload', 'dashboard', 'reports', 'settings', 'admin'].map(perm => (
                                                <Grid item xs={6} key={perm}>
                                                    <FormControlLabel
                                                        control={
                                                            <Checkbox
                                                                checked={formData.permissions[perm] || false}
                                                                onChange={() => handlePermissionChange(perm)}
                                                            />
                                                        }
                                                        label={perm.charAt(0).toUpperCase() + perm.slice(1)}
                                                    />
                                                </Grid>
                                            ))}
                                        </Grid>
                                    </FormGroup>
                                </Paper>
                            </Box>
                        </Stack>
                    </DialogContent>
                    <DialogActions sx={{ p: 3 }}>
                        <Button onClick={handleCloseModal} color="inherit">
                            Cancelar
                        </Button>
                        <Button type="submit" variant="contained" color="primary">
                            {editingUser ? 'Salvar Alterações' : 'Criar Usuário'}
                        </Button>
                    </DialogActions>
                </form>
            </Dialog>
        </Box>
    );
};

export default AdminPage;
